import { escapeHtml } from '@carbonwebmail/domain';
/** Cells carry their own padding: margins are unreliable in Outlook. */
const CELL = 'padding:12px 24px;font-family:Arial,Helvetica,sans-serif;';
const TEXT = `${CELL}font-size:15px;line-height:23px;color:#344054;`;
/** A row of columns. `role="presentation"` keeps it out of a screen reader's table list. */
function section(widths) {
    const cells = widths
        .map((width) => `<td class="cw-cell" width="${width}%" valign="top" style="${TEXT}">Drop content here</td>`)
        .join('');
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cells}</tr></table>`;
}
/** One cell holding one thing, which is how every content block is dropped. */
function block(inner, style = CELL) {
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="${style}">${inner}</td></tr></table>`;
}
/**
 * The links inside a navigation or social row.
 *
 * Exported because the builder rebuilds the row from this whenever somebody
 * edits the list — adding a fourth link has to produce exactly the markup the
 * block was dropped with, and two places writing that markup is two places for
 * it to drift. Anchors only, so the builder can replace a row's children
 * without replacing the row it has selected.
 *
 * The gap sits on every link but the last, because a trailing margin pushes the
 * row off centre in the clients that honour it.
 */
export function emailLinks(kind, items) {
    return items
        .map((item, index) => {
        const gap = index === items.length - 1 ? '' : `margin-right:${LINK_GAP[kind]};`;
        return `<a data-cw="item" href="${escapeHtml(item.href)}" style="${gap}${LINK_STYLE[kind]}">${escapeHtml(item.label)}</a>`;
    })
        .join('');
}
const LINK_GAP = { navigation: '18px', social: '12px' };
const LINK_STYLE = {
    navigation: 'font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#344054;text-decoration:none;',
    social: 'font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#667085;text-decoration:underline;',
};
/**
 * A row of links, in the box the builder edits as a list.
 *
 * The box is what carries the role and the kind, so a link added later is
 * styled like the three it joins rather than like the other row's.
 */
function linkRow(kind, items) {
    return block(`<div data-cw="items" data-cw-items="${kind}">${emailLinks(kind, items)}</div>`, 'padding:12px 24px;');
}
const NAVIGATION = [
    { label: 'Shop', href: 'https://' },
    { label: 'About', href: 'https://' },
    { label: 'Contact', href: 'https://' },
];
const SOCIAL = [
    { label: 'LinkedIn', href: 'https://' },
    { label: 'Instagram', href: 'https://' },
    { label: 'X', href: 'https://' },
];
/**
 * The sections on offer, as the widths they make.
 *
 * Even splits up to six, plus the two uneven ones people actually reach for: a
 * wide column with a narrow one beside it, and its mirror. Built from the list
 * rather than written out, so a section's drawing, its label and its markup
 * cannot disagree about how many columns it has.
 */
const SECTIONS = [
    ['section-1', '1 column', [100]],
    ['section-2', '2 columns', [50, 50]],
    ['section-3', '3 columns', [33, 34, 33]],
    ['section-4', '4 columns', [25, 25, 25, 25]],
    ['section-5', '5 columns', [20, 20, 20, 20, 20]],
    ['section-6', '6 columns', [16, 17, 17, 17, 17, 16]],
    ['section-wide-left', 'Wide and narrow', [70, 30]],
    ['section-wide-right', 'Narrow and wide', [30, 70]],
];
export const EMAIL_BLOCKS = [
    // ---------------------------------------------------------------- sections
    ...SECTIONS.map(([id, label, columns]) => ({
        id,
        label,
        group: 'sections',
        icon: 'columns',
        columns,
        content: section(columns),
    })),
    // ------------------------------------------------------------------ blocks
    {
        id: 'title',
        label: 'Title',
        group: 'blocks',
        icon: 'title',
        content: block('<h1 style="margin:0;font-size:26px;line-height:34px;color:#101828;font-weight:700;">Your headline</h1>'),
    },
    {
        id: 'text',
        label: 'Text',
        group: 'blocks',
        icon: 'text',
        content: block('<p style="margin:0;">Write something here. Use the toolbar above to change how it looks.</p>', TEXT),
    },
    {
        id: 'image',
        label: 'Image',
        group: 'blocks',
        icon: 'image',
        // A placeholder rather than an invented url. The address is a setting on
        // the block; an image that 404s in every inbox is worse than an obvious gap.
        content: block('<img alt="" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;background-color:#eaecf0;" src="">', 'padding:12px 24px;'),
    },
    {
        id: 'video',
        label: 'Video',
        group: 'blocks',
        icon: 'video',
        // A picture that links out, not an embed: no mail client plays video, and
        // one that tried would be stripped. The anchor is a wrapper rather than
        // words, so it takes an address and nothing is typed into it.
        content: block('<a data-cw="wrap" href="https://" style="display:block;text-decoration:none;"><img alt="Watch the video" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;background-color:#101828;" src=""></a>', 'padding:12px 24px;'),
    },
    {
        id: 'button',
        label: 'Button',
        group: 'blocks',
        icon: 'button',
        // A padded anchor inside a table cell. Every other way of making a button
        // is a div with a background, and Outlook does not give it one. The table
        // is the button, which is what moves left or right; the anchor is the words
        // written on it.
        content: block('<table data-cw="button" role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" bgcolor="#0f9d9d" style="border-radius:24px;"><a data-cw="link" href="https://" style="display:inline-block;padding:12px 28px;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;border-radius:24px;">Read more</a></td></tr></table>', 'padding:16px 24px;'),
    },
    {
        id: 'logo',
        label: 'Logo',
        group: 'blocks',
        icon: 'logo',
        content: block('<img alt="Logo" width="160" style="display:block;height:auto;border:0;background-color:#eaecf0;" src="">', 'padding:20px 24px;'),
    },
    {
        id: 'social',
        label: 'Social',
        group: 'blocks',
        icon: 'social',
        content: linkRow('social', SOCIAL),
    },
    {
        id: 'navigation',
        label: 'Navigation',
        group: 'blocks',
        icon: 'navigation',
        content: linkRow('navigation', NAVIGATION),
    },
    {
        id: 'divider',
        label: 'Divider',
        group: 'blocks',
        icon: 'divider',
        // A bordered cell rather than an `hr`: clients style `hr` differently and
        // some ignore its colour entirely.
        content: block('<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid #eaecf0;font-size:0;line-height:0;">&nbsp;</td></tr></table>', 'padding:8px 24px;'),
    },
    {
        id: 'spacer',
        label: 'Spacer',
        group: 'blocks',
        icon: 'spacer',
        // Zero font-size and a non-breaking space: an empty cell collapses.
        content: block('&nbsp;', 'height:24px;font-size:0;line-height:0;'),
    },
    {
        id: 'html',
        label: 'HTML',
        group: 'blocks',
        icon: 'html',
        /*
         * A box whose contents are markup, not words.
         *
         * It used to drop a paragraph explaining itself, which is all it ever was:
         * anything typed into it was typed as text and arrived as text, so a block
         * called HTML showed `<table>` to the reader instead of drawing one. The
         * role is what tells the builder to treat what is put here as markup and
         * parse it, and the paragraph below is only what an empty one says.
         */
        content: block('<div data-cw="code"><p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#667085;">Put your own markup in this block’s settings. It goes through the same allowlist as everything else before it is sent.</p></div>', TEXT),
    },
    {
        id: 'product',
        label: 'Product',
        group: 'blocks',
        icon: 'product',
        /*
         * A card somebody fills in, not a feed.
         *
         * Brevo's version pulls from a product catalogue. There is no catalogue
         * here, and a block that silently rendered nothing would be worse than
         * none — so this is the layout, with the picture, the name, the price and
         * the link left to be written.
         */
        content: block('<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center"><img alt="" width="240" style="display:block;width:100%;max-width:240px;height:auto;border:0;background-color:#eaecf0;" src=""><p style="margin:12px 0 4px 0;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:700;color:#101828;">Product name</p><p style="margin:0 0 12px 0;font-family:Arial,Helvetica,sans-serif;font-size:15px;color:#667085;">£00.00</p><a data-cw="link" href="https://" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#00b0c2;text-decoration:underline;">View it</a></td></tr></table>', 'padding:16px 24px;'),
    },
    {
        id: 'unsubscribe',
        label: 'Unsubscribe',
        group: 'blocks',
        icon: 'unsubscribe',
        /*
         * Offered even though the footer carries one anyway.
         *
         * Every message gets an unsubscribe link appended whether or not this block
         * is used — that is not optional and not a setting. This exists for a design
         * that wants one somewhere else as well, and it fills from the same
         * placeholder, so the two cannot point at different things. It has a role of
         * its own because it is the one link whose address is not a setting: the
         * words can be reworded, the destination cannot.
         */
        content: block('<a data-cw="unsubscribe" href="{{unsubscribe_url}}" style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#98a2b3;text-decoration:underline;">Unsubscribe</a>', 'padding:12px 24px;'),
    },
];
//# sourceMappingURL=emailBlocks.js.map